import { DownOutlined } from '@ant-design/icons';
import { Input, InputNumber, Select } from 'antd';
import classnames from 'classnames';
import { get, isEqual } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useState } from 'react';
import * as uuid from 'uuid';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { DiffType } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff-modal';
import styles from '@/presentation/pages/setting/app-deliver-config/config-modal/edit/diff.module.scss';
import { CUSTOMIZE_CONFIG } from '@/presentation/pages/setting/app-deliver-config/constant';
import { customizeDataTransform, isLong } from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';

interface IProps {
  config: RecordType;
  fatherNameOption?: { label: string, value: string }[];
  typeDisabled?: boolean;
  fatherNameValue?: string;
  childName?: RecordType;
  diffConfig: RecordType;
  diffType: DiffType;
}

export enum  IType {
  FLOAT = 'float',
  INT = 'int',
  BOOL = 'bool',
  STRING = 'string',
  OBJECT = 'object',
  ARRAY = 'array',
}

interface IData {
  key: string;
  name: string;
  value?: string | boolean | number;
  type: IType;
  children?: IData[];
  isEqualName?: boolean;
  isEqualType?: boolean;
  isEqualValue?: boolean;
}


interface IJsonItem {
  data: IData;
  fatherType: IType;
  typeDisabled?: boolean;
  fatherNameOption?: { label: string, value: string }[];
  fatherNameValue?: string;
  childName?: RecordType;
  diffType: DiffType;
}
const arrayOrObject = [IType.OBJECT, IType.ARRAY];

const numberTypes = [IType.INT, IType.FLOAT];

const typeOption = [
  { value: IType.STRING, label: IType.STRING },
  { value: IType.FLOAT, label: IType.FLOAT },
  { value: IType.INT, label: IType.INT },
  { value: IType.BOOL, label: IType.BOOL },
  { value: IType.OBJECT, label: IType.OBJECT },
  { value: IType.ARRAY, label: IType.ARRAY },
];
const boolOption = [
  { value: true, label: 'true' },
  { value: false, label: 'false' },
];
// json对象转自定义数据
const  jsonTransformCustomize = (
  values: any, name: string, diffValues?: any, diffName?: string,
  equal?: boolean, configFieldInfo?: Record<string, any>,
): IData => {
  const configFieldInfoType = configFieldInfo ? isLong(get(configFieldInfo, `${name}.type`)) : undefined;
  const type = configFieldInfoType ? configFieldInfoType : customizeDataTransform(values);
  const configFieldInfoDiffType = configFieldInfo ? isLong(get(configFieldInfo, `${name}.type`)) : undefined;
  const diffType = diffValues === undefined ? undefined : configFieldInfoDiffType || customizeDataTransform(diffValues);
  const res: IData = {
    key: uuid.v4(),
    name,
    type,
    isEqualName: equal ? equal : isEqual(name, diffName),
    isEqualType: equal ? equal : isEqual(type, diffType),
  };
  if (Array.isArray(values)) {
    res.value = undefined;
    res.isEqualValue = true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    res.children = values.map((item: any, index) => {
      const diffItem = Array.isArray(diffValues) ? diffValues.find(diffItem => isEqual(item, diffItem)) : undefined;
      return jsonTransformCustomize(
        item,
        'item',
        diffItem ?? Array.isArray(diffValues) ? diffValues?.[index] : undefined,
        (diffValues?.[index] === undefined || !Array.isArray(diffValues)) && diffItem === undefined ? undefined : 'item',
        diffItem !== undefined, configFieldInfo,
      );
    });
  } else if (values instanceof Object) {
    const keys = Object.keys(values || {});
    const diffKeys = diffValues instanceof Object ? Object.keys?.(diffValues) : undefined;
    res.value = undefined;
    res.isEqualValue = true;
    res.children = keys.map((item: any, index) => {
      const diffItem = diffValues?.[item];
      return jsonTransformCustomize(
        values[item],
        item,
        diffItem ?? (diffKeys ? diffValues?.[diffKeys?.[index]] : undefined),
        diffItem === undefined ? diffKeys?.[index] : item,
        isEqual(item, diffItem), configFieldInfo,
      );
    }) || [];
  } else {
    res.value = values;
    res.isEqualValue = equal ? equal : isEqual(values, diffValues);
  }
  return res;
};

export const DiffCustomizeData: FC<IProps> = observer((props: IProps) => {
  const { config,
    typeDisabled,
    fatherNameOption,
    fatherNameValue,
    childName, diffConfig, diffType } = props;
  const rootName = Object.keys(config)?.[0] || 'customize';
  const {
    configs: operation = {},
  } = useStores().ConfigStore;
  const { configFieldInfo = {} } = operation;
  const customizeData = jsonTransformCustomize(
    config?.[rootName],
    rootName, diffConfig?.[rootName], rootName, isEqual(config?.[rootName], diffConfig?.[rootName]),
    rootName === CUSTOMIZE_CONFIG.name ? undefined : configFieldInfo,
  );
  return <div className={styles.customize}>
    <div>
      {customizeData?.children?.map(item => <JsonItem
        key={item.key}
        data={item}
        fatherType={IType.OBJECT}
        typeDisabled={typeDisabled}
        fatherNameOption={fatherNameOption}
        fatherNameValue={fatherNameValue}
        childName={childName}
        diffType={diffType}
      />)}
    </div>
  </div>;
});

const JsonItem: FC<IJsonItem> = ({ data,
  fatherType,
  typeDisabled,
  fatherNameOption,
  fatherNameValue,
  diffType,
  childName }) => {
  const { children: dataChildren } = data;
  const [visible, setVisible] = useState(true);
  const [type] = useState(data.type);
  const downClick = () => {
    setVisible(visible => !visible);
  };

  const isPrev = diffType === DiffType.PREV;

  const nameClass = classnames({
    [styles.diffPrev]: isPrev && !data.isEqualName,
    [styles.diffNext]: !isPrev && !data.isEqualName,
    [styles.customizeName]: true,
  });
  const typeClass = classnames({
    [styles.customizeType]: true,
    [styles.diffPrev]: isPrev && !data.isEqualType,
    [styles.diffNext]: !isPrev && !data.isEqualType,
    [styles.flexAuto]: arrayOrObject.includes(type),
  });
  const valueClass = classnames({
    [styles.diffPrev]: isPrev && !data.isEqualValue,
    [styles.diffNext]: !isPrev && !data.isEqualValue,
    [styles.customizeValue]: true,
  });
  return <div className={styles.customizeItem}>
    <div className={styles.jsonItem}>
      {arrayOrObject.includes(type) ? <div className={styles.itemIcon}>
        <div onClick={downClick} className={classnames({ [styles.downOutlined]: true,
          [styles.rightOutlined]: !visible,
        })}><DownOutlined /></div></div> : <div className={styles.downOutlined}></div>}
      {fatherNameOption
        ? <div
          title={fatherNameValue}
          className={nameClass}>
          {<Select value={fatherNameValue}
                   showArrow={false}
                   disabled={fatherType === IType.ARRAY || true}
                   options={fatherNameOption} />}
        </div>
        : null}
      {childName && !fatherNameOption ? <div
        className={nameClass} title={data.name}>
        {<Select value={data.name}
                 showArrow={false}
                 disabled={fatherType === IType.ARRAY || true}
                 options={Object.keys(childName).map(item => ({ value: item, label: item }))}
                  />}
      </div> : null}
      {!childName && !fatherNameOption ? <div title={data.name}
        className={nameClass}>
        <Input value={data.name} disabled={fatherType === IType.ARRAY || true} />
      </div> : null}

      <div
        className={typeClass} title={data.type}>
        {typeDisabled
          ? <Input value={data.type} disabled={true} />
          : <Select showArrow={false}
                    value={data.type} disabled={true} options={typeOption} />}
      </div>

      {type === IType.STRING && (data.value === undefined || typeof(data.value) === 'string')
        ? <div
          title={data.value}
          className={valueClass}>
          <Input disabled={true} value={data.value !== undefined ? String(data.value) : undefined}
                 />
        </div>
        : null}
      {numberTypes.includes(type) && (data.value === undefined || typeof(data.value) === 'number')
        ? <div
          className={valueClass}
          title={data.value !== undefined ? String(data.value) : ''}
        >
          <InputNumber
            value={data.value}
            disabled={true}
            precision={type === IType.INT ? 0 : undefined}/>
        </div>
        : null}
      {type === IType.BOOL && (data.value === undefined || typeof(data.value) === 'boolean')
        ? <div className={valueClass} title={data.value !== undefined ? String(data.value) : ''}>
          <Select showArrow={false}
                  disabled={true}
                  value={data.value}
                  options={boolOption}/>
        </div>
        : null}
    </div>
    {dataChildren?.length && visible
      ? dataChildren.map(item => <JsonItem key={item.key}
                                           diffType={diffType}
                                           typeDisabled={typeDisabled}
                                           fatherType={type}
                                           childName={childName}
                                           data={item} />) : undefined}
  </div>;
};


